import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Layout, Modal, Space, Switch, Table } from 'antd';
import { FC, useEffect, useState } from 'react';

import { SpecialtyDto, SpecialtyUpdateDto } from '@clh/api-client';
import { useApiClient } from '@clh/ui';

import { useToast } from '../hooks/use-toast';
import { Content, Header } from '../layout';
import Spinner from '../spinner/spinner';

export const Specialties: FC = () => {
    const api = useApiClient();
    const { notification } = useToast();
    const [specialties, setSpecialties] = useState<SpecialtyDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSpecialty, setEditingSpecialty] =
        useState<SpecialtyDto | null>(null);
    const [form] = Form.useForm();

    const fetchSpecialties = async () => {
        if (!api) return;
        try {
            setLoading(true);
            const response = await api.specialtyControllerGetSpecialties({
                includeInactive: true,
            });
            setSpecialties(response);
        } catch (_error) {
            notification.error({
                message: 'Failed to fetch specialties',
                duration: 3,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchSpecialties();
    }, [api]);

    const handleAddSpecialty = () => {
        setEditingSpecialty(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEditSpecialty = (specialty: SpecialtyDto) => {
        setEditingSpecialty(specialty);
        form.setFieldsValue({
            code: specialty.code,
            name: specialty.name,
            assessmentIds: specialty.assessmentIds.join(','),
            active: specialty.active,
        });
        setIsModalVisible(true);
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const updateDto: SpecialtyUpdateDto = {
                code: values.code,
                name: values.name,
                assessmentIds: values.assessmentIds
                    .split(',')
                    .map((id: string) => id.trim())
                    .map(Number),
                active: values.active,
            };

            await api?.specialtyControllerUpdateSpecialty({
                specialtyUpdateDto: updateDto,
            });

            notification.success({
                message: `Specialty ${
                    editingSpecialty ? 'updated' : 'created'
                } successfully`,
                duration: 3,
            });
            setIsModalVisible(false);
            void fetchSpecialties();
        } catch (_error) {
            notification.error({
                message: 'Failed to save specialty',
                duration: 3,
            });
        }
    };

    const columns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            render: (active: boolean) => <Switch checked={active} disabled />,
        },
        {
            title: 'Assessment IDs',
            dataIndex: 'assessmentIds',
            key: 'assessmentIds',
            render: (assessmentIds: string[]) =>
                assessmentIds.length > 0 ? assessmentIds.join(', ') : 'None',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: SpecialtyDto) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEditSpecialty(record)}
                    />
                </Space>
            ),
        },
    ];

    if (!api || loading) {
        return <Spinner />;
    }

    return (
        <Layout>
            <Header>
                <h2>Specialties</h2>
            </Header>
            <Content>
                <div style={{ marginBottom: 16 }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddSpecialty}
                    >
                        Add Specialty
                    </Button>
                </div>

                <Table
                    dataSource={specialties}
                    columns={columns}
                    rowKey="code"
                />

                <Modal
                    title={
                        editingSpecialty ? 'Edit Specialty' : 'Add Specialty'
                    }
                    open={isModalVisible}
                    onOk={() => void handleModalOk()}
                    onCancel={() => setIsModalVisible(false)}
                    okText={editingSpecialty ? 'Update' : 'Create'}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="code"
                            label="Code"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter a code',
                                },
                            ]}
                        >
                            <Input
                                disabled={!!editingSpecialty}
                                placeholder="e.g. MED_SURG"
                            />
                        </Form.Item>
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter a name',
                                },
                            ]}
                        >
                            <Input placeholder="e.g. Medical-Surgical" />
                        </Form.Item>
                        <Form.Item
                            name="active"
                            label="Active"
                            valuePropName="checked"
                            initialValue={true}
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="assessmentIds"
                            label="Assessment IDs"
                            help="Enter assessment IDs separated by commas"
                            rules={[
                                {
                                    required: true,
                                    message:
                                        'Please enter at leat one assessment id',
                                },
                            ]}
                        >
                            <Input placeholder="e.g. 123, 456, 789" />
                        </Form.Item>
                    </Form>
                </Modal>
            </Content>
        </Layout>
    );
};
